// ./src/pages/private/EventSchedulePage/RecurrenceRuleEditor.tsx
//
// Edits the recurrence RULE for a recurring event (stored in
// event.settings.recurrence). The server generates the occurrences from it.
// Reuses the app's WeekDaySelector / MonthDaySelector. Weekday numbering is
// converted here: the selectors use ISO (1=Mon..7=Sun); the rule/API use
// 0=Sun..6=Sat (dayjs .day()).

import React, { useState } from 'react';
import { Stack, Group, Text, Divider, Button, ActionIcon, Alert } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';

import { RecurrenceRule, RECURRENCE_FREQ } from '@/utils/api';
import { AppIcon, SegmentedField, SelectField, NumberField, DateTimeField, SwitchField } from '@/components/generic';
import { WeekDaySelector, MonthDaySelector } from '@/components/generic';

const isoToDow = (iso: number): number => (iso === 7 ? 0 : iso); // ISO Mon..Sun → 0=Sun..6=Sat
const dowToIso = (dow: number): number => (dow === 0 ? 7 : dow);

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_OPTIONS = MONTHS.map((label, i) => ({ value: String(i + 1), label }));

// "HH:mm" ↔ minutes-since-midnight helpers for keep-duration on the pattern times.
const timeToMin = (t: string): number => {
  const [h, m] = t.split(':').map((n) => Number(n));
  return h * 60 + m;
};
const minToTime = (min: number): string => {
  const m = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

export interface RecurrenceRuleEditorProps {
  value: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
  /**
   * The event's time zone — the "until" DATE is a day on the event's calendar (the server generates dates up to it
   * there), so "not in the past" is today THERE (ROADMAP #93). ⚠ Required.
   */
  timeZone: string | null;
}

export const RecurrenceRuleEditor: React.FC<RecurrenceRuleEditorProps> = ({ value, onChange, timeZone }) => {
  const { t } = useTranslation(['events', 'common']);
  const patch = (p: Partial<RecurrenceRule>) => onChange({ ...value, ...p });

  // Yearly = explicit (month, day) pairs (Feb 3, Jul 5, Sep 25).
  const yearDays = value.byYearDay ?? [];
  const setYearDays = (arr: { month: number; day: number }[]) => patch({ byYearDay: arr });
  const addYearDay = () => setYearDays([...yearDays, { month: 1, day: 1 }]);
  const updateYearDay = (i: number, p: Partial<{ month: number; day: number }>) =>
    setYearDays(yearDays.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  const removeYearDay = (i: number) => setYearDays(yearDays.filter((_, idx) => idx !== i));

  // Kept in state, not derived — otherwise picking "On date" with no date yet
  // (until still null) would snap the control straight back to "Never".
  const [endMode, setEndMode] = useState<'never' | 'on' | 'after'>(
    value.until ? 'on' : value.count ? 'after' : 'never',
  );

  const intervalUnit =
    value.freq === RECURRENCE_FREQ.WEEKLY
      ? t('events:schedulePage.weeks', { defaultValue: 'week(s)' })
      : value.freq === RECURRENCE_FREQ.MONTHLY
        ? t('events:schedulePage.months', { defaultValue: 'month(s)' })
        : t('events:schedulePage.years', { defaultValue: 'year(s)' });

  // Start time keeps the duration (or seeds +1h when there's no end yet); the end
  // can never land before the start.
  const onStartTime = (val: string) => {
    if (!val) {
      patch({ startTime: null });
      return;
    }
    const oldStart = value.startTime;
    const end = value.endTime;
    if (end && oldStart) {
      patch({ startTime: val, endTime: minToTime(timeToMin(end) + (timeToMin(val) - timeToMin(oldStart))) });
    } else if (!end) {
      patch({ startTime: val, endTime: minToTime(timeToMin(val) + 60) });
    } else {
      patch({ startTime: val });
    }
  };
  const onEndTime = (val: string) => {
    if (!val) {
      patch({ endTime: null });
      return;
    }
    if (value.startTime && timeToMin(val) < timeToMin(value.startTime)) {
      patch({ endTime: value.startTime }); // don't allow end before start
    } else {
      patch({ endTime: val });
    }
  };

  return (
    <Stack gap="md">
      <SegmentedField
        value={value.freq}
        onChange={(_n, v) => patch({ freq: v as RecurrenceRule['freq'] })}
        data={[
          { value: RECURRENCE_FREQ.WEEKLY, label: t('events:schedulePage.weekly', { defaultValue: 'Weekly' }) },
          { value: RECURRENCE_FREQ.MONTHLY, label: t('events:schedulePage.monthly', { defaultValue: 'Monthly' }) },
          { value: RECURRENCE_FREQ.YEARLY, label: t('events:schedulePage.yearly', { defaultValue: 'Yearly' }) },
        ]}
      />

      <Group align="flex-end" gap="sm" wrap="nowrap">
        <NumberField
          label={t('events:schedulePage.every', { defaultValue: 'Repeat every' })}
          value={value.interval}
          onChange={(_n, v) => patch({ interval: typeof v === 'number' && v > 0 ? v : 1 })}
          min={1}
          max={99}
          w={140}
        />
        <Text size="md" pb={10} c="dimmed">
          {intervalUnit}
        </Text>
      </Group>

      {value.freq === RECURRENCE_FREQ.WEEKLY && (
        <Stack gap={6}>
          <Text fw={600} size="md" c="gray.8">
            {t('events:schedulePage.onDays', { defaultValue: 'On these days' })}
          </Text>
          <WeekDaySelector
            value={(value.byWeekday ?? []).map(dowToIso)}
            onChange={(iso) => patch({ byWeekday: iso.map(isoToDow).sort((a, b) => a - b) })}
          />
        </Stack>
      )}

      {value.freq === RECURRENCE_FREQ.MONTHLY && (
        <Stack gap={6}>
          <Text fw={600} size="md" c="gray.8">
            {t('events:schedulePage.onDates', { defaultValue: 'On these dates' })}
          </Text>
          <MonthDaySelector
            value={(value.byMonthday ?? []).map(String)}
            onChange={(days) => patch({ byMonthday: days.map(Number).filter((n) => n >= 1 && n <= 31) })}
            showLastDay={false}
          />
        </Stack>
      )}

      {value.freq === RECURRENCE_FREQ.YEARLY && (
        <Stack gap="xs">
          <Text fw={600} size="md" c="gray.8">
            {t('events:schedulePage.onTheseDates', { defaultValue: 'On these dates each year' })}
          </Text>
          {yearDays.length === 0 && (
            <Text c="dimmed">
              {t('events:schedulePage.yearDaysEmpty', {
                defaultValue: 'Add the dates it repeats on — e.g. Feb 3, Jul 5, Sep 25.',
              })}
            </Text>
          )}
          {yearDays.map((d, i) => (
            <Group key={i} gap="sm" wrap="nowrap" align="flex-end">
              <SelectField
                label={i === 0 ? t('events:schedulePage.month', { defaultValue: 'Month' }) : undefined}
                data={MONTH_OPTIONS}
                value={String(d.month)}
                onChange={(_n, v) => updateYearDay(i, { month: Number(v) || 1 })}
                allowDeselect={false}
                w={150}
              />
              <NumberField
                label={i === 0 ? t('events:schedulePage.day', { defaultValue: 'Day' }) : undefined}
                value={d.day}
                onChange={(_n, v) => updateYearDay(i, { day: typeof v === 'number' ? Math.min(31, Math.max(1, v)) : 1 })}
                min={1}
                max={31}
                w={110}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                onClick={() => removeYearDay(i)}
                aria-label={t('common:buttons.remove', { defaultValue: 'Remove' })}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Button
            variant="light"
            leftSection={<AppIcon iconName="Plus" size={16} />}
            onClick={addYearDay}
            style={{ alignSelf: 'flex-start' }}
          >
            {t('events:schedulePage.addYearDay', { defaultValue: 'Add a date' })}
          </Button>
        </Stack>
      )}

      <Group gap="sm" grow align="flex-start">
        <TimeInput
          label={t('events:schedulePage.startTime', { defaultValue: 'Start time' })}
          value={value.startTime ?? ''}
          onChange={(e) => onStartTime(e.currentTarget.value)}
        />
        <TimeInput
          label={t('events:schedulePage.endTime', { defaultValue: 'End time' })}
          value={value.endTime ?? ''}
          onChange={(e) => onEndTime(e.currentTarget.value)}
        />
      </Group>

      <Text c="dimmed">
        {t('events:schedulePage.dstHint', {
          defaultValue:
            'Times are the event’s local time — they stay the same across a daylight-saving change (only the UTC offset shifts).',
        })}
      </Text>

      <SwitchField
        label={t('events:schedulePage.rsvpEachOccurrence', {
          defaultValue: 'Guests can RSVP to each occurrence',
        })}
        checked={value.rsvpEnabled !== false}
        onChange={(_n, checked) => patch({ rsvpEnabled: checked })}
      />

      <Divider />

      <Stack gap={6}>
        <Text fw={600} size="md" c="gray.8">
          {t('events:schedulePage.ends', { defaultValue: 'Ends' })}
        </Text>
        <SegmentedField
          value={endMode}
          onChange={(_n, v) => {
            setEndMode(v as 'never' | 'on' | 'after');
            if (v === 'never') patch({ until: null, count: null });
            else if (v === 'on') patch({ count: null }); // keep until (may still be empty)
            else patch({ count: value.count ?? 10, until: null });
          }}
          data={[
            { value: 'never', label: t('events:schedulePage.endsNever', { defaultValue: 'Never' }) },
            { value: 'on', label: t('events:schedulePage.endsOn', { defaultValue: 'On date' }) },
            { value: 'after', label: t('events:schedulePage.endsAfter', { defaultValue: 'After' }) },
          ]}
        />
        {endMode === 'never' && (
          <Alert variant="light" color="blue" icon={<AppIcon iconName="InfoCircle" size={18} />}>
            {value.freq === RECURRENCE_FREQ.YEARLY
              ? t('events:schedulePage.neverHintYearly', {
                  defaultValue:
                    'With no end date, the next 3 years of dates are generated each time you generate.',
                })
              : t('events:schedulePage.neverHint', {
                  defaultValue:
                    'With no end date, only the next 6 months of dates are generated each time you generate.',
                })}
          </Alert>
        )}
        {endMode === 'on' && (
          <DateTimeField
            label={t('events:schedulePage.endDate', { defaultValue: 'End date' })}
            value={value.until ?? null}
            onChange={(_n, v) => patch({ until: v })}
            dateOnly
            disallowPast
            timeZone={timeZone}
            clearable
            maw={260}
          />
        )}
        {endMode === 'after' && (
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <NumberField
              label={t('events:schedulePage.count', { defaultValue: 'Number of dates' })}
              value={value.count ?? 10}
              onChange={(_n, v) => patch({ count: typeof v === 'number' && v > 0 ? v : 1 })}
              min={1}
              max={500}
              w={160}
            />
            <Text size="md" pb={10} c="dimmed">
              {t('events:schedulePage.occurrences', { defaultValue: 'occurrences' })}
            </Text>
          </Group>
        )}
      </Stack>
    </Stack>
  );
};
